import React, { FC, useCallback, useRef, useState, useEffect } from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import { Box, BoxProps } from '@mui/material';
import Typography from 'components/Typography/Typography';
import { IntlShape } from 'react-intl';

import * as styles from './ProjectsList.styles';

import { definedTranslation } from '../../../../../i18n';
import { ProjectButton } from '../../ProjectButton';

interface ProjectsListProps extends BoxProps {
  projects: ProjectModel[];
  selectedProjectId?: number | null;
  onProjectButtonClick?(project: ProjectModel): void;
  intl: IntlShape;
}

export const ProjectsList: FC<ProjectsListProps> = (props) => {
  const {
    projects = [],
    selectedProjectId,
    onProjectButtonClick,
    intl,
    ...boxProps
  } = props;

  const [fadeShown, setFadeShown] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkScrollPosition = useCallback(() => {
    if (!contentRef.current) {
      return;
    }

    const scrollYPosition = Math.ceil(contentRef.current.scrollTop);
    const height = Math.ceil(contentRef.current.getBoundingClientRect().height);
    const scrollHeight = contentRef.current.scrollHeight;

    setFadeShown(scrollHeight - scrollYPosition > height);
  }, [projects]);

  useEffect(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  const onScroll = useCallback(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  return (
    <Box sx={styles.root} {...boxProps}>
      <Box ref={contentRef} onScroll={onScroll} sx={styles.list}>
        {projects.map((project) => (
          <ProjectButton
            key={project.id}
            project={project}
            selected={selectedProjectId === project.externalId}
            onClick={onProjectButtonClick}
          >
            {project.title}
          </ProjectButton>
        ))}
        {projects.length === 0 && (
          <Typography
            variant="h5"
            fontWeight="600"
            component="p"
            sx={styles.notResultsText}
          >
            {intl?.formatMessage(definedTranslation.noResultsMessage) ||
              'No Results'}
          </Typography>
        )}
      </Box>
      {fadeShown && <Box sx={styles.bottomFade} />}
    </Box>
  );
};
